import { useState, useContext } from "react";
import { useApolloClient } from "@apollo/client";
import { BookingContext } from "../context/BookingContext";
import { Types } from "../context/search.reducer";
import { DataQuery, Interval, IntervalFilterInput } from "@models/ISchema";
import intervalsQuery from "@services/collections/intervals/graphql/intervalsItems.query.graphql";
import useSelectedWeekIntervals from "./useSelectedWeekIntervals";

interface useGetIntervalsProps {
  shouldSaveContext?: boolean;
}

const useGetIntervals = (
  props?: useGetIntervalsProps,
): [
  { data: boolean; intervals: Interval[]; error: boolean; loading: boolean },
  (scenario: string, filter?: IntervalFilterInput, useCache?: boolean) => void,
] => {
  const { shouldSaveContext = true } = props || {};
  const client = useApolloClient();
  const { dispatch, state } = useContext(BookingContext);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [intervals, setIntervals] = useState<Array<Interval> | null>(null);
  const [selectedWeekIntervals] = useSelectedWeekIntervals(state?.search?.currentBooking?.sessions);

  const getIntervals = async (scenario: string, filter?: IntervalFilterInput) => {
    try {
      setLoading(true);
      const { data } = await client.query({
        query: intervalsQuery,
        variables: {
          scenarioId: scenario,
          filter,
        },
        fetchPolicy: "no-cache",
      });
      const dataQuery: DataQuery = data?.data;
      const intervals = dataQuery?.intervals?.items || [];
      const intervalWeeks = intervals.map((week, index) => {
        return {
          ...week,
          selected: week?.id in selectedWeekIntervals,
          value: (index + 1).toString(),
        };
      });
      shouldSaveContext && dispatch({ type: Types.SetIntervals, payload: intervalWeeks });
      setIntervals(intervalWeeks);
      setLoading(false);
    } catch (error) {
      console.error("[getIntervals]", error);
      shouldSaveContext && dispatch({ type: Types.SetIntervals, payload: [] });
      setIntervals(null);
      setResult(true);
      setError(true);
      setLoading(false);
    }
  };

  const getCacheIntervals = async (
    scenario: string,
    filter?: IntervalFilterInput,
    useCache = true,
  ) => {
    if (!useCache) {
      getIntervals(scenario, filter);
      return;
    }

    try {
      setLoading(true);
      const variablesClassRoom = {
        query: intervalsQuery,
        variables: {
          scenarioId: scenario,
          filter,
        },
        fetchPolicy: "no-cache",
      };
      const { data } = await client.readQuery(variablesClassRoom);
      const dataQuery: DataQuery = data?.data;
      const intervals = dataQuery?.intervals?.items || [];
      const intervalWeeks = intervals.map((week, index) => {
        return {
          ...week,
          selected: week?.id in selectedWeekIntervals,
          value: (index + 1).toString(),
        };
      });
      shouldSaveContext && dispatch({ type: Types.SetIntervals, payload: intervalWeeks });
      setIntervals(intervalWeeks);
      setLoading(false);
    } catch (error) {
      getIntervals(scenario, filter);
    }
  };

  return [{ data: result, intervals, error, loading }, getCacheIntervals];
};

export default useGetIntervals;
